/**
 * safe-get-operations.ts — queue #645 §UAT-2026-07-07-R645 §R645-3.
 *
 * `SAFE_GET_OPERATIONS` — try-it-out 이 호출을 허용하는 **FE 하드코딩 allowlist**. 자유 URL
 * 입력은 금지되고, 이 목록에 있는 operation 만 선택 가능하며 각 operation 이 선언한 쿼리
 * 파라미터만 폼 필드로 노출된다. 전 항목 **부작용 없는 조회 GET**(§R645-1 실측) — 쓰기
 * (POST/PUT/DELETE·multipart)·`rate-limit-demo`(토큰소진)·`export`(GitLab+rate-limit)·
 * `auth-check`(RequestBody) 는 의도적으로 이 목록에 없다(§R645-3 ★쓰기 계열 전면 제외).
 *
 * R640 `facet-predicates.ts` 패턴과 동형 — 순수 데이터 + 순수 함수만, BE 호출 없음.
 */

export interface SafeGetParam {
  /** 쿼리 파라미터명 */
  name: string;
  /** 필수 여부 — 필수 파라미터 미입력 시 폼은 empty 상태로 호출을 막는다 */
  required: boolean;
}

export interface SafeGetOperation {
  key: string;
  method: 'GET';
  path: string;
  params: SafeGetParam[];
  /** 이 operation 과 매핑되는 카탈로그 노드 code 목록(§R645-3 표) */
  nodeCodes: string[];
}

export const SAFE_GET_OPERATIONS: Record<string, SafeGetOperation> = {
  modules: {
    key: 'modules',
    method: 'GET',
    path: '/showcase/modules',
    params: [{ name: 'artifact', required: false }],
    nodeCodes: ['C001', 'C002', 'C005', 'C006'],
  },
  'masking-policy': {
    key: 'masking-policy',
    method: 'GET',
    path: '/showcase/masking-policy',
    params: [],
    nodeCodes: ['C013'],
  },
  cache: {
    key: 'cache',
    method: 'GET',
    path: '/showcase/cache',
    params: [{ name: 'key', required: true }],
    nodeCodes: ['C051'],
  },
  'jpa-items': {
    key: 'jpa-items',
    method: 'GET',
    path: '/showcase/jpa/items',
    params: [
      { name: 'page', required: false },
      { name: 'size', required: false },
      { name: 'sort', required: false },
    ],
    nodeCodes: ['C042'],
  },
  'route-match': {
    key: 'route-match',
    method: 'GET',
    path: '/showcase/route-match',
    params: [
      { name: 'method', required: true },
      { name: 'path', required: true },
    ],
    nodeCodes: ['G001'],
  },
  'health-check-demo': {
    key: 'health-check-demo',
    method: 'GET',
    path: '/showcase/health-check-demo',
    params: [{ name: 'routeId', required: true }],
    nodeCodes: ['G007'],
  },
  'kafka-status': {
    key: 'kafka-status',
    method: 'GET',
    path: '/showcase/demo/kafka/status',
    params: [],
    nodeCodes: ['C055'],
  },
  'rabbitmq-status': {
    key: 'rabbitmq-status',
    method: 'GET',
    path: '/showcase/demo/rabbitmq/status',
    params: [],
    nodeCodes: ['C057'],
  },
  'mongo-status': {
    key: 'mongo-status',
    method: 'GET',
    path: '/showcase/demo/mongo/status',
    params: [],
    nodeCodes: ['C054'],
  },
  'elasticsearch-status': {
    key: 'elasticsearch-status',
    method: 'GET',
    path: '/showcase/demo/elasticsearch/status',
    params: [],
    nodeCodes: ['C053'],
  },
  'otel-status': {
    key: 'otel-status',
    method: 'GET',
    path: '/showcase/demo/otel/status',
    params: [],
    nodeCodes: ['C072'],
  },
  'outbox-status': {
    key: 'outbox-status',
    method: 'GET',
    path: '/showcase/demo/outbox/status',
    params: [],
    nodeCodes: ['C056'],
  },
  'oauth2-status': {
    key: 'oauth2-status',
    method: 'GET',
    path: '/showcase/demo/oauth2/status',
    params: [],
    nodeCodes: ['C035'],
  },
};

/** code → operationKey[] 역인덱스(순수 함수) — 노드에 매핑된 안전 GET operation 이 있는지 조회. */
export function operationsForCode(code: string): SafeGetOperation[] {
  return Object.values(SAFE_GET_OPERATIONS).filter((op) => op.nodeCodes.includes(code));
}

/** 노드에 try-it-out 진입점을 활성화할지(매핑된 안전 GET operation 존재 여부). */
export function hasTryItOut(code: string): boolean {
  return operationsForCode(code).length > 0;
}
